import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { DateField } from "./DateField";

/**
 * Modal form for a guest to join a cottage's waitlist. Prefills email/name
 * from the logged-in user and check-in/check-out from the parent booking form.
 */
export function WaitlistDialog({
  open, onClose,
  plotId, plotLabel,
  defaultFrom, defaultTo,
  userEmail, userName,
}) {
  const [form, setForm] = useState({ email: "", name: "", from_date: "", to_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        email: userEmail || "",
        name: userName || "",
        from_date: defaultFrom || "",
        to_date: defaultTo || "",
        note: "",
      });
    }
  }, [open, userEmail, userName, defaultFrom, defaultTo]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/waitlist", { plot_id: plotId, ...form });
      toast.success("Added to waitlist — we'll email if it frees up");
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not join waitlist");
    } finally { setBusy(false); }
  };

  const invalid = !form.email || !form.from_date || !form.to_date;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="waitlist-modal">
        <DialogHeader>
          <DialogTitle>Join the waitlist</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {plotLabel ? `Plot ${plotLabel.plot_number} · ${plotLabel.project_name || ""}` : "Cottage"} —
            we'll email you the moment a booking on these dates gets cancelled.
          </p>
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wl-name-input" /></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="wl-email-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <DateField label="From" value={form.from_date} onChange={(v) => setForm({ ...form, from_date: v })} testid="wl-from-input" />
            <DateField label="To" value={form.to_date} onChange={(v) => setForm({ ...form, to_date: v })} minDate={form.from_date} testid="wl-to-input" />
          </div>
          <div><Label>Note (optional)</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Any flexibility on dates?" data-testid="wl-note-input" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="wl-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={busy || invalid} data-testid="wl-submit-btn">
              {busy ? "Adding…" : "Add me"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
